import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, TwitterLogo, LinkedinLogo, Calendar, MapPin, ArrowUpRight,
  Sparkle, EnvelopeSimple, X, PlayCircle, Translate,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { api } from "@/lib/api";

export default function SpeakerDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    api.get(`/speakers/${id}`).then((r) => setS(r.data)).catch(() => setS(false));
  }, [id]);

  if (s === null) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (s === false) return <div className="px-6 md:px-10 lg:px-16 py-16 text-zinc-500">{t("common.notFound")}</div>;

  // JSON-LD structured data — Person schema for rich Google speaker cards
  const siteOrigin = (typeof window !== "undefined" ? window.location.origin : "https://conferencely.org");
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": s.name,
    "jobTitle": s.title || undefined,
    "worksFor": s.organization ? { "@type": "Organization", "name": s.organization } : undefined,
    "description": s.bio || undefined,
    "image": s.image_url || undefined,
    "url": `${siteOrigin}/speakers/${id}`,
    "sameAs": [
      s.website,
      s.twitter ? `https://twitter.com/${(s.twitter || "").replace(/^@/, "")}` : null,
      s.linkedin,
    ].filter(Boolean),
    "knowsAbout": Array.isArray(s.expertise) ? s.expertise : (s.expertise ? [s.expertise] : undefined),
    "knowsLanguage": Array.isArray(s.languages) ? s.languages : undefined,
  };

  return (
    <div data-testid="speaker-detail" className="px-6 md:px-10 lg:px-16 py-10 md:py-12">
      <script
        type="application/ld+json"
        data-testid="jsonld-person"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd)
            .replace(/</g, "\\u003c")
            .replace(/-->/g, "--\\u003e")
            .replace(/\u2028/g, "\\u2028")
            .replace(/\u2029/g, "\\u2029"),
        }}
      />
      <Link to="/speakers" data-testid="back-to-speakers" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-10">
        <ArrowLeft size={14} /> {t("speakersPage.title")}
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <div className="aspect-[3/4] bg-zinc-100 border border-zinc-200 overflow-hidden relative">
            {s.is_featured && (
              <div data-testid="detail-featured-badge" className="absolute top-3 left-3 bg-blue-700 text-white text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 flex items-center gap-1.5 z-10">
                <Sparkle size={10} weight="fill" /> {t("speakerDetail.featuredSpeaker")}
              </div>
            )}
            {s.image_url
              ? <img src={s.image_url} alt={s.name} className="img-mono w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center font-display text-9xl font-black text-zinc-300 bg-grid">{s.name[0]}</div>}
          </div>

          <div className="mt-5 flex flex-col gap-2 font-mono text-xs">
            {s.location && <span className="flex items-center gap-2 text-zinc-600"><MapPin size={14} /> {s.location}</span>}
            {s.website && <a href={s.website} target="_blank" rel="noreferrer" data-testid="speaker-website" className="flex items-center gap-2 text-zinc-600 hover:text-blue-700"><Globe size={14} /> {safeHost(s.website)} <ArrowUpRight size={10} /></a>}
            {s.twitter && <a href={`https://twitter.com/${(s.twitter || "").replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid="speaker-twitter" className="flex items-center gap-2 text-zinc-600 hover:text-blue-700"><TwitterLogo size={14} /> @{(s.twitter || "").replace(/^@/, "")}</a>}
            {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" data-testid="speaker-linkedin" className="flex items-center gap-2 text-zinc-600 hover:text-blue-700"><LinkedinLogo size={14} /> LinkedIn <ArrowUpRight size={10} /></a>}
            {s.video_reel_url && <a href={s.video_reel_url} target="_blank" rel="noreferrer" data-testid="speaker-reel" className="flex items-center gap-2 text-zinc-600 hover:text-blue-700"><PlayCircle size={14} /> {t("speakerDetail.videoReel")} <ArrowUpRight size={10} /></a>}
          </div>

          {/* Booking CTA */}
          {s.available_for_booking && (
            <button
              data-testid="open-contact-btn"
              type="button"
              onClick={() => setContactOpen(true)}
              className="btn-primary w-full justify-center mt-6"
            >
              <EnvelopeSimple size={16} /> {t("speakerDetail.contactSpeaker")}
            </button>
          )}
        </div>

        <div className="md:col-span-7">
          {s.organization && <div className="overline mb-3">{s.organization}</div>}
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-[.95]" data-testid="speaker-name">{s.name}</h1>
          {s.title && <div className="text-lg text-zinc-600 mt-3">{s.title}</div>}

          {/* Meta strip */}
          {((s.expertise || []).length > 0 || (s.languages || []).length > 0 || s.speaking_fee) && (
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-xs font-mono text-zinc-600" data-testid="speaker-meta-strip">
              {(s.expertise || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {s.expertise.map((x) => (
                    <span key={x} className="border border-zinc-300 px-2 py-1 uppercase tracking-wider">{x}</span>
                  ))}
                </div>
              )}
              {(s.languages || []).length > 0 && (
                <div className="flex items-center gap-1.5"><Translate size={13} /> <span>{s.languages.join(" · ")}</span></div>
              )}
              {s.speaking_fee && (
                <div className="flex items-center gap-1.5 text-zinc-500"><span className="text-zinc-400 uppercase tracking-wider">{t("speakerDetail.fee")}:</span> <span className="text-zinc-950">{s.speaking_fee}</span></div>
              )}
            </div>
          )}

          <div className="mt-10 border-t border-zinc-950 pt-6">
            <div className="overline mb-4">{t("speakerDetail.bio")}</div>
            <p className="text-base leading-relaxed text-zinc-700 whitespace-pre-line max-w-prose">{s.bio || t("speakerDetail.noBio")}</p>
          </div>

          {(s.talk_topics || []).length > 0 && (
            <div className="mt-10 border-t border-zinc-200 pt-6" data-testid="speaker-talks">
              <div className="overline mb-4">{t("speakerDetail.talkTopics")}</div>
              <ul className="space-y-2 text-base text-zinc-700 list-disc list-inside">
                {s.talk_topics.map((t2) => <li key={t2}>{t2}</li>)}
              </ul>
            </div>
          )}

          {s.conferences?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-end justify-between border-b border-zinc-950 pb-3 mb-5">
                <h2 className="font-display font-bold text-2xl tracking-tight">{t("speakerDetail.appearances")}</h2>
                <span className="font-mono text-xs text-zinc-500">{s.conferences.length}</span>
              </div>
              <div className="border border-zinc-200 divide-y divide-zinc-200">
                {s.conferences.map((c, i) => (
                  <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`speaker-conf-${i}`} className="block px-5 py-4 hover:bg-[#FAFAFA] group">
                    <div className="flex items-start gap-4">
                      <div className="font-mono text-[10px] text-zinc-400 pt-1 tracking-[0.15em]">№ {String(i + 1).padStart(2, "0")}</div>
                      <div className="flex-1">
                        <div className="font-display font-semibold text-base group-hover:text-blue-700 transition-colors">{c.title}</div>
                        <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-4 font-mono">
                          {c.start_date && <span className="flex items-center gap-1"><Calendar size={11} /> {format(new Date(c.start_date), "MMM d, yyyy")}</span>}
                          {(c.city || c.country) && <span className="flex items-center gap-1"><MapPin size={11} /> {c.city}{c.country ? `, ${c.country}` : ""}</span>}
                        </div>
                      </div>
                      <ArrowUpRight size={14} data-arrow="forward" className="text-zinc-300 group-hover:text-blue-700 transition-colors mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {contactOpen && <ContactModal speaker={s} onClose={() => setContactOpen(false)} />}
    </div>
  );
}

function safeHost(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

function ContactModal({ speaker, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [delivered, setDelivered] = useState(true);

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/speakers/${speaker.id}/contact`, form);
      setDelivered(res?.data?.delivered !== false);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("speakerDetail.contactFailed"));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" data-testid="contact-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border-2 border-zinc-950 max-w-lg w-full p-7">
        <button onClick={onClose} data-testid="contact-modal-close" className="absolute top-3 end-3 p-1.5 hover:bg-zinc-100"><X size={18} /></button>
        {sent ? (
          <div className="text-center py-6" data-testid="contact-sent">
            <div className="w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center mx-auto mb-4">
              <Sparkle size={26} weight="fill" className="text-blue-700 dark:text-blue-400" />
            </div>
            <h2 className="font-display font-bold text-2xl mb-2" data-testid="contact-sent-title">{t("speakerDetail.contactSentTitle", { name: speaker.name })}</h2>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              {delivered
                ? t("speakerDetail.contactSentBody", { name: speaker.name })
                : t("speakerDetail.contactSavedBody", { name: speaker.name, defaultValue: `Your message is safely saved. {{name}} will see it in their Symposia inbox and follow up with you directly.` })}
            </p>

            {/* Typical response window + reply-to preview — sets expectations */}
            <div className="border border-zinc-200 dark:border-zinc-800 p-4 text-start bg-[#FAFAFA] dark:bg-zinc-950/60" data-testid="contact-response-window">
              <div className="flex items-start gap-3">
                <EnvelopeSimple size={16} className="text-blue-700 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
                    {t("speakerDetail.responseWindowLabel", { defaultValue: "Typical response window" })}
                  </div>
                  <div className="text-sm font-medium text-zinc-950 dark:text-zinc-100">
                    {t("speakerDetail.responseWindowValue", { defaultValue: "Speakers usually reply within 2–5 business days" })}
                  </div>
                  <div className="text-xs font-mono text-zinc-500 dark:text-zinc-400 mt-2 leading-relaxed">
                    {delivered
                      ? t("speakerDetail.replyToNote", { email: form.email, defaultValue: `Their reply will land in {{email}}. We use Reply-To so you get their answer directly — Symposia never sits in the middle.` })
                      : t("speakerDetail.replyToNoteSaved", { email: form.email, defaultValue: `When {{name}} replies, it will land in {{email}}.`, name: speaker.name })}
                  </div>
                </div>
              </div>
            </div>

            <button onClick={onClose} data-testid="contact-sent-close" className="btn-primary mt-6 w-full justify-center">
              {t("common.close", { defaultValue: "Close" })}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="overline mb-1 flex items-center gap-1.5"><EnvelopeSimple size={11} /> {t("speakerDetail.contactTitle")}</div>
            <h2 className="font-display font-bold text-3xl tracking-tight leading-tight">{t("speakerDetail.contactHeading", { name: speaker.name })}</h2>
            <p className="text-xs font-mono text-zinc-500">{t("speakerDetail.contactSubtitle")}</p>

            <div>
              <label className="overline block mb-1">{t("speakerDetail.yourName")}</label>
              <input data-testid="contact-name" required value={form.name} onChange={(e) => setF("name", e.target.value)} className="input-box" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">{t("speakerDetail.yourEmail")}</label>
                <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setF("email", e.target.value)} className="input-box" />
              </div>
              <div>
                <label className="overline block mb-1">{t("speakerDetail.organization")}</label>
                <input data-testid="contact-org" value={form.organization} onChange={(e) => setF("organization", e.target.value)} className="input-box" />
              </div>
            </div>
            <div>
              <label className="overline block mb-1">{t("speakerDetail.message")}</label>
              <textarea data-testid="contact-message" required minLength={10} value={form.message} onChange={(e) => setF("message", e.target.value)} className="input-box min-h-[120px]" placeholder={t("speakerDetail.messagePlaceholder")} />
            </div>
            <button data-testid="contact-send-btn" type="submit" disabled={busy} className="btn-primary w-full justify-center">
              {busy ? t("common.loading") : t("speakerDetail.sendInquiry")} <ArrowUpRight size={14} data-arrow="forward" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
